// The Unabated Angle §F8 consumption — read hook over the anonymised
// crowd-attention index via get_fixture_attention(). Auth-gated,
// counts-only (the RPC never returns identity). Soft-fails; never
// throws. HONEST DORMANT: the index is server-gated (≥5 distinct
// watchers) so this returns [] for almost every fixture today and
// thickens automatically as real engagement accrues — it never
// fabricates attention.

import { useCallback, useEffect, useMemo, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";
import {
  pickAttention,
  reviewFlagged,
  type FixtureAttention,
} from "./fixtureAttention.ts";

export interface UseFixtureAttentionState {
  rows: readonly FixtureAttention[];
  /** Attention for one fixture, or null when below the floor. */
  forFixture: (fixtureId: string) => FixtureAttention | null;
  /** Fixtures the crowd flagged for a model confidence review. */
  reviewFlagged: readonly FixtureAttention[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useFixtureAttention(): UseFixtureAttentionState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const [rows, setRows] = useState<readonly FixtureAttention[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    const { data, error: rpcErr } = await (supabase.rpc as unknown as (
      fn: string,
      args: Record<string, unknown>,
    ) => Promise<{ data: FixtureAttention[] | null; error: { message: string } | null }>)(
      "get_fixture_attention",
      { p_fixture_id: null },
    );
    if (rpcErr) {
      // Soft-fail: an error ⇒ no attention shown (never a guess).
      setError(rpcErr.message);
      setRows([]);
      setLoading(false);
      return;
    }
    setRows(Array.isArray(data) ? data : []);
    setLoading(false);
  }, [supabase]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return useMemo(
    () => ({
      rows,
      forFixture: (fixtureId: string) => pickAttention(rows, fixtureId),
      reviewFlagged: reviewFlagged(rows),
      loading,
      error,
      refetch,
    }),
    [rows, loading, error, refetch],
  );
}
